import React from 'react';
import { Typography } from 'antd';
import { fct } from '../../fct/tokens/tokens';
import type { RespuestaEncuesta } from '../../api/cxPanelApi';

const { Text } = Typography;

/*
 * Respuestas de una F4 con el MISMO cuestionario que rellena Mystery en su portal (5 bloques, textos literales de
 * FCTSiteClientes.app · frontend/src/encuestas/cuestionario.ts, spec §3.3). Así CxCp lee la encuesta igual que la
 * ve Mystery, y no una lista de códigos. Si cambia el cuestionario del portal, cambiarlo aquí también.
 */

type Pregunta =
  | { tipo: 'valoracion'; campo: string; texto: string; max: 5 | 10 }
  | { tipo: 'sino'; campo: string; texto: string }
  | { tipo: 'texto'; campo: string; texto: string }
  | { tipo: 'casillas'; texto: string; opciones: { campo: string; texto: string }[] };

interface Bloque { numero: number; titulo: string; instruccion?: string; preguntas: Pregunta[] }

const BLOQUES: Bloque[] = [
  {
    numero: 1, titulo: 'Momentos Clave del Proceso', instruccion: 'Valore su satisfacción en cada fase del proceso (escala 1-5)',
    preguntas: [
      { tipo: 'valoracion', max: 5, campo: 'F4_MomentosClave_Inicio', texto: 'Inicio y análisis de la compraventa' },
      { tipo: 'valoracion', max: 5, campo: 'F4_MomentosClave_asesoramiento', texto: 'Asesoramiento durante la negociación' },
      { tipo: 'valoracion', max: 5, campo: 'F4_MomentosClave_Equipo', texto: 'Acompañamiento del equipo jurídico, financiero y fiscal' },
      { tipo: 'valoracion', max: 5, campo: 'F4_MomentosClave_Escritura', texto: 'Gestión de la escritura' },
      { tipo: 'valoracion', max: 5, campo: 'F4_MomentosClave_Sensacion', texto: 'Sensación posterior a la operación (tranquilidad / seguridad)' },
      { tipo: 'sino', campo: 'F4_MomentosClave_Preocupacion', texto: '¿Hubo alguna fase del proceso que le generara especial preocupación o incertidumbre?' },
      { tipo: 'texto', campo: 'F4_MomentosClave_PreocupacionTxt', texto: '¿Cuál?' },
    ],
  },
  {
    numero: 2, titulo: 'Eficacia y Seguridad', instruccion: 'Indique su grado de acuerdo (escala 1-5)',
    preguntas: [
      { tipo: 'valoracion', max: 5, campo: 'F4_EficaciaSeg_Proteccion', texto: 'He tenido la sensación de que Farmaconsulting protegía mis intereses' },
      { tipo: 'valoracion', max: 5, campo: 'F4_EficaciaSeg_Confianza', texto: 'He confiado en el asesoramiento recibido incluso en momentos críticos' },
      { tipo: 'valoracion', max: 5, campo: 'F4_EficaciaSeg_Objetivos', texto: 'Farmaconsulting ha conseguido mis objetivos en la forma y plazo esperados' },
    ],
  },
  {
    numero: 3, titulo: 'NPS - Recomendación',
    preguntas: [
      { tipo: 'valoracion', max: 10, campo: 'F4_NPS_Recomendacion', texto: '¿Recomendaría Farmaconsulting a otros farmacéuticos? (0-10)' },
      {
        tipo: 'casillas', texto: '¿Cuál es el principal motivo de su puntuación?',
        opciones: [
          { campo: 'F4_NPS_Cercania', texto: 'Cercanía y trato personal' },
          { campo: 'F4_NPS_Claridad', texto: 'Claridad en las explicaciones' },
          { campo: 'F4_NPS_Disponibilidad', texto: 'Disponibilidad cuando lo necesitaba' },
          { campo: 'F4_NPS_Equipo', texto: 'Equipo especializado en cada área' },
          { campo: 'F4_NPS_Eficacia', texto: 'Eficacia' },
          { campo: 'F4_NPS_Seguridad', texto: 'Seguridad en el proceso' },
        ],
      },
      { tipo: 'texto', campo: 'F4_NPS_MotivoOtros', texto: 'Otros motivos' },
    ],
  },
  {
    numero: 4, titulo: 'Valor percibido - Satisfacción con el precio de la operación',
    preguntas: [
      { tipo: 'sino', campo: 'F4_Valor_SatisfechoPrecio', texto: 'En relación con el precio de su farmacia, ¿Está satisfecho con el precio al que ha comprado/vendido su farmacia?' },
      { tipo: 'texto', campo: 'F4_Valor_Opinion', texto: '¿Cómo crees que se podría haber alcanzado un mayor valor?' },
    ],
  },
  {
    numero: 5, titulo: 'Mejora dirigida - Aspectos a mejorar en el servicio',
    preguntas: [
      {
        tipo: 'casillas', texto: 'Si tuviera que mejorar un aspecto del servicio, ¿cuál sería?',
        opciones: [
          { campo: 'F4_Mejora_Comunicacion', texto: 'Comunicación' },
          { campo: 'F4_Mejora_Claridad', texto: 'Claridad inicial de expectativas (responsabilidades de FCT vs cliente)' },
          { campo: 'F4_Mejora_Agilidad', texto: 'Agilidad del proceso' },
          { campo: 'F4_Mejora_Coordinacion', texto: 'Coordinación entre profesionales' },
          { campo: 'F4_Mejora_Informacion', texto: 'Información fiscal/jurídica' },
          { campo: 'F4_Mejora_Ninguna', texto: 'Ninguna' },
        ],
      },
      { tipo: 'texto', campo: 'F4_Mejora_Otros', texto: 'Otro (especificar)' },
    ],
  },
];

/** Campos que pinta el cuestionario (el resto de respuestas de la encuesta van aparte, al final). */
const CAMPOS = new Set(BLOQUES.flatMap((b) => b.preguntas.flatMap((p) => (p.tipo === 'casillas' ? p.opciones.map((o) => o.campo) : [p.campo]))));

const limpio = (v?: string | null) => (v == null ? '' : String(v).trim());
const esSi = (v?: string | null) => ['1', 'true', 'sí', 'si', 'yes'].includes(limpio(v).toLowerCase());
const esNo = (v?: string | null) => ['0', 'false', 'no'].includes(limpio(v).toLowerCase());
const numero = (v?: string | null): number | null => {
  const n = Number.parseFloat(limpio(v).replace(',', '.'));
  return Number.isFinite(n) ? Math.round(n) : null;
};

/** Escala 1-5 (o 0-10) con la opción elegida marcada, como los botones del formulario. */
const Escala: React.FC<{ max: 5 | 10; valor: number | null }> = ({ max, valor }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
    {Array.from({ length: max === 10 ? 11 : 5 }, (_, i) => (max === 10 ? i : i + 1)).map((n) => {
      const elegida = n === valor;
      return (
        <span
          key={n}
          className="fct-mono"
          style={{
            minWidth: 28, height: 28, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 6,
            fontSize: 13, fontWeight: elegida ? 700 : 400,
            background: elegida ? fct.marca['verde-oscuro'] : fct.superficie.tarjeta,
            color: elegida ? fct.tinta.inversa : fct.tinta['500'],
            border: `1px solid ${elegida ? fct.marca['verde-oscuro'] : fct.linea.tarjeta}`,
          }}
        >
          {n}
        </span>
      );
    })}
    {valor == null && <Text type="secondary" style={{ marginInlineStart: 6 }}>Sin contestar</Text>}
  </div>
);

const SiNo: React.FC<{ valor?: string | null }> = ({ valor }) => {
  const opcion = (texto: string, elegida: boolean) => (
    <span
      style={{
        padding: '2px 12px', borderRadius: 999, fontSize: 13, fontWeight: elegida ? 700 : 400,
        background: elegida ? fct.marca['verde-oscuro'] : fct.superficie.tarjeta,
        color: elegida ? fct.tinta.inversa : fct.tinta['500'],
        border: `1px solid ${elegida ? fct.marca['verde-oscuro'] : fct.linea.tarjeta}`,
      }}
    >
      {texto}
    </span>
  );
  if (!esSi(valor) && !esNo(valor)) return <Text type="secondary">Sin contestar</Text>;
  return <div style={{ display: 'flex', gap: 6 }}>{opcion('Sí', esSi(valor))}{opcion('No', esNo(valor))}</div>;
};

/** Respuestas de una F4 en el formato del cuestionario de Mystery. */
const RespuestasF4: React.FC<{ respuestas: RespuestaEncuesta[]; otras: (r: RespuestaEncuesta[]) => React.ReactNode }> = ({ respuestas, otras }) => {
  const valor = new Map(respuestas.map((r) => [r.pregunta, r.respuesta ?? null]));
  const fila = (texto: string, contenido: React.ReactNode, clave: string) => (
    <div
      key={clave}
      style={{
        display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 16, alignItems: 'center',
        padding: '10px 0', borderTop: `1px solid ${fct.linea.fila}`,
      }}
    >
      <span style={{ color: fct.tinta['900'] }}>{texto}</span>
      <div>{contenido}</div>
    </div>
  );
  const restantes = respuestas.filter((r) => !CAMPOS.has(r.pregunta));

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {BLOQUES.map((b) => (
        <section key={b.numero} style={{ border: `1px solid ${fct.linea.tarjeta}`, borderRadius: 8, padding: '12px 16px', background: fct.superficie.tarjeta }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4 }}>
            <span className="fct-mono" style={{ fontWeight: 700, color: fct.marca['verde-oscuro'] }}>{b.numero}</span>
            <span style={{ fontWeight: 600, color: fct.tinta['900'] }}>{b.titulo}</span>
          </div>
          {b.instruccion && <Text type="secondary" style={{ fontSize: 12.5 }}>{b.instruccion}</Text>}
          {b.preguntas.map((p, i) => {
            if (p.tipo === 'valoracion') return fila(p.texto, <Escala max={p.max} valor={numero(valor.get(p.campo))} />, p.campo);
            if (p.tipo === 'sino') return fila(p.texto, <SiNo valor={valor.get(p.campo)} />, p.campo);
            if (p.tipo === 'texto') {
              const t = limpio(valor.get(p.campo));
              if (!t) return null;   // los textos solo se enseñan si se contestaron (en el portal dependen de otra respuesta)
              return (
                <div key={p.campo} style={{ padding: '10px 0', borderTop: `1px solid ${fct.linea.fila}` }}>
                  <div style={{ color: fct.tinta['900'], marginBottom: 6 }}>{p.texto}</div>
                  <div style={{ padding: '8px 12px', background: fct.superficie.hundida, borderRadius: 6, whiteSpace: 'pre-wrap' }}>{t}</div>
                </div>
              );
            }
            return (
              <div key={`casillas-${b.numero}-${i}`} style={{ padding: '10px 0', borderTop: `1px solid ${fct.linea.fila}` }}>
                <div style={{ color: fct.tinta['900'], marginBottom: 8 }}>{p.texto}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {p.opciones.map((o) => {
                    const marcada = esSi(valor.get(o.campo));
                    return (
                      <span
                        key={o.campo}
                        style={{
                          padding: '3px 10px', borderRadius: 6, fontSize: 13,
                          background: marcada ? fct.marca['verde-oscuro'] : fct.superficie.tarjeta,
                          color: marcada ? fct.tinta.inversa : fct.tinta['500'],
                          border: `1px solid ${marcada ? fct.marca['verde-oscuro'] : fct.linea.tarjeta}`,
                          fontWeight: marcada ? 600 : 400,
                        }}
                      >
                        {marcada ? '☑ ' : '☐ '}{o.texto}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      ))}
      {restantes.length > 0 && (
        <section>
          <div style={{ fontWeight: 600, color: fct.tinta['700'], margin: '4px 0 8px' }}>Otras respuestas de la encuesta</div>
          {otras(restantes)}
        </section>
      )}
    </div>
  );
};

export default RespuestasF4;
